// scheduling/pages/Reporting.jsx
// The Reporting section shell (Trello #309).
//
// Owns three things and no report logic:
//   1. the capability probe (/api/rprsReports?action=my_access)
//   2. the tab strip, built from reportRegistry ∩ what the server granted
//   3. ?tab= deeplinking
//
// Individual reports are plain components rendered into the active tab. They receive
// nothing from here — each fetches its own data and re-checks its own access
// server-side, because this shell hiding a tab is a courtesy, not a control.

import { useEffect, useMemo, useState } from 'react';
import { useUrlState } from '../../hooks/useUrlState';
import { BRAND } from '../utils/schedConstants';
import { accessibleReports } from '../utils/reportRegistry';
import { fetchMyReportAccess } from '../services/reportsApi';
import CapacityReport from './CapacityReport';
import GenTrackReport from '../components/reports/GenTrackReport';
import SignInsReport from '../components/reports/SignInsReport';
import AdHocReport from '../components/reports/AdHocReport';
import TerminatedReport from '../components/reports/TerminatedReport';
import NicheReport from '../components/reports/NicheReport';
import UnscheduledHoursReport from '../components/reports/UnscheduledHoursReport';
import LookaheadReport from '../components/reports/LookaheadReport';
import SvaReport from '../components/reports/SvaReport';

// tab slug -> component. Only built reports appear; the registry's `ready` flag keeps
// unbuilt ones out of the tab strip, so a slug reaching here always has a component.
const TAB_COMPONENTS = {
  capacity: CapacityReport,
  gentrack: GenTrackReport,
  signins: SignInsReport,
  adhoc: AdHocReport,
  terminated: TerminatedReport,
  niches: NicheReport,
  unscheduled: UnscheduledHoursReport,
  lookahead: LookaheadReport,
  sva: SvaReport,
};

export default function Reporting() {
  const [access, setAccess] = useState(null);   // null = still loading
  const [error, setError] = useState(null);

  // Empty default rather than a real slug: which tab is "first" depends on the probe,
  // which hasn't answered on the first render. Deliberately NOT using useUrlState's
  // `validate` option — it memoizes on the raw param only, so a validate list that
  // arrives later (as this one does) would never be re-applied and a deeplinked
  // ?tab=capacity would be silently discarded.
  const [tabParam, setTabParam] = useUrlState('tab', '');

  useEffect(() => {
    let cancelled = false;
    fetchMyReportAccess()
      .then(r => { if (!cancelled) setAccess(r); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  const tabs = useMemo(() => accessibleReports(access?.reports), [access]);

  // Resolve the active tab against what's actually available. An unknown or
  // now-inaccessible ?tab= falls back to the first tab rather than rendering blank —
  // stale links and bookmarks are expected here, since these URLs get shared.
  const activeTab = useMemo(() => {
    if (!tabs.length) return null;
    return tabs.some(t => t.tab === tabParam) ? tabParam : tabs[0].tab;
  }, [tabs, tabParam]);

  const active = tabs.find(t => t.tab === activeTab);
  const ActiveComponent = activeTab ? TAB_COMPONENTS[activeTab] : null;

  return (
    <div className="sched-emp-root">
      <div className="sched-emp-header">
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: BRAND.deepTeal }}>Reporting</div>
          {active?.description && (
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{active.description}</div>
          )}
        </div>
      </div>

      {tabs.length > 1 && (
        <div className="sched-tabs">
          {tabs.map(t => (
            <button key={t.tab}
              className={`sched-tab ${activeTab === t.tab ? 'sched-tab--active' : ''}`}
              onClick={() => setTabParam(t.tab)}
              title={t.description}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 40px' }}>
        {error && (
          <div style={{ padding: 24, color: '#DC2626', fontSize: 13 }}>
            Couldn’t load reporting access: {error}
          </div>
        )}

        {!error && access === null && (
          <div style={{ padding: 24, color: '#6B7280', fontSize: 13 }}>Loading…</div>
        )}

        {/* Reached by typing the URL directly — nav hides the link otherwise. Says
            nothing about which reports exist, so it can't be used to enumerate them. */}
        {!error && access !== null && tabs.length === 0 && (
          <div style={{ padding: 24, color: '#6B7280', fontSize: 13 }}>
            You don’t have access to any reports.
          </div>
        )}

        {ActiveComponent && <ActiveComponent />}
      </div>
    </div>
  );
}
